import { Clipboard } from '@angular/cdk/clipboard';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { TooltipDirective } from '@catch/directives';
import {
  ConfirmModalComponent,
  DropdownComponent,
  InfoBannerComponent,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { environment } from 'apps/company-tool/src/environments/environment';
import { JobsAdapter } from '../../adapters/jobs.adapter';
import { AppState } from '../../app.reducer';
import { JobState } from '../../enums/job-state.enum';
import { ProcessStepType } from '../../enums/process-step-type.enum';
import { SlotType } from '../../enums/slot-type.enum';
import { Job } from '../../interfaces/job';
import { JobStep } from '../../interfaces/job-step';
import { JobTypes, JobsService } from '../../services/company/jobs.service';
import { FontawesomeService } from '../../services/fontawesome.service';
import { SidenavService } from '../../services/sidenav.service';
import { selectDisabledModeActive } from '../../store/auth/auth.reducer';
import { selectCompanyAccount } from '../../store/company-account/company-account.reducer';
import { selectPreselectSlotType } from '../../store/subscription/subscription.reducer';
import { SlotSelectorComponent } from '../slot-selector/slot-selector.component';

@UntilDestroy()
@Component({
  selector: 'app-job-actions-dropdown',
  imports: [
    FontAwesomeModule,
    TranslocoPipe,
    DropdownComponent,
    TooltipDirective,
    ConfirmModalComponent,
    InfoBannerComponent,
    SlotSelectorComponent,
    RouterModule,
  ],
  templateUrl: './job-actions-dropdown.component.html',
  styleUrl: './job-actions-dropdown.component.scss',
})
export class JobActionsDropdownComponent {
  readonly fontawesomeService = inject(FontawesomeService);
  public disabled = input<boolean>(false);
  public jobId = input<number>();
  public jobsService = inject(JobsService);
  private store = inject(Store<AppState>);
  private sidenavService = inject(SidenavService);
  private companyAccount = this.store.selectSignal(selectCompanyAccount);
  public job = signal<Job | null>(null);
  public currentAction = signal<string | null>(null);
  readonly actionDisabled = computed(
    () => this.disabled() || this.currentAction() !== null,
  );
  /**
   * Whether the job can be deleted. Uses live process-step counts when this
   * dropdown is shown for the currently opened job, otherwise `job.deletable`.
   */
  readonly isJobDeletable = computed(() => {
    const job = this.job();
    if (!job) {
      return false;
    }
    const jobId = this.jobId();
    const liveProcessSteps = this.sidenavService.jobSteps();
    if (
      jobId != null &&
      this.sidenavService.jobId() == jobId &&
      liveProcessSteps.length > 0
    ) {
      return !this.hasOpenStepApplications(liveProcessSteps);
    }
    return job.deletable === true;
  });
  public jobLink = computed(
    () =>
      `${environment.jobboardBaseUrl}/companies/${this.companyAccount()?.companyID}/jobs/${this.jobId()}`,
  );
  public toastService = inject(ToastsService);
  private clipboard = inject(Clipboard);
  private jobsAdapter = inject(JobsAdapter);
  readonly openJobSlotSelection = signal<SlotType | null>(null);
  readonly changeSlotModalSelection = signal<SlotType | null>(null);
  readonly preselectSlotType = this.store.selectSignal(selectPreselectSlotType);
  public JobState = JobState;
  public SlotType = SlotType;
  public disabledModeActive = this.store.selectSignal(selectDisabledModeActive);
  public alignment = input<string>('right');
  readonly isProduction = environment.production;
  readonly bookDistribution = output<Job>();

  constructor() {
    effect(() => {
      const jobId = this.jobId();
      this.jobsService.currentJobsRevision();
      if (!jobId) {
        this.job.set(null);
        return;
      }
      if (this.jobsService.currentJobs.length > 0) {
        const loadedJob = this.jobsService.getJobById(jobId);
        if (loadedJob) {
          this.job.set(loadedJob);
        } else {
          this.jobsAdapter
            .getJob(jobId)
            .pipe(untilDestroyed(this))
            .subscribe((job) => {
              job.id = jobId;
              this.job.set(job);
            });
        }
        return;
      }
      this.jobsService.load(JobTypes.CURRENT).then(() => {
        const loadedJob = this.jobsService.getJobById(jobId);
        if (loadedJob) {
          this.job.set(loadedJob);
          return;
        }
        this.jobsAdapter
          .getJob(jobId)
          .pipe(untilDestroyed(this))
          .subscribe((job) => {
            job.id = jobId;
            this.job.set(job);
          });
      });
    });
  }

  /**
   * Reloads the job so delete-eligibility matches current process-step occupancy.
   * @returns {void}
   */
  refreshJobOnDropdownOpen(): void {
    const jobId = this.jobId();
    if (!jobId) {
      return;
    }
    this.jobsAdapter
      .getJob(jobId)
      .pipe(untilDestroyed(this))
      .subscribe((job: Job) => {
        job.id = jobId;
        const cachedJob = this.jobsService.getJobById(jobId);
        if (cachedJob) {
          cachedJob.deletable = job.deletable === true;
        }
        this.job.set({ ...(this.job() ?? job), ...job, id: jobId });
      });
  }

  /**
   * Returns whether any inbox, pre-qualification, or individual step still has applications.
   * @param {JobStep[]} processSteps - Process steps with application counts
   * @returns {boolean} True when at least one open selection step has applications
   */
  private hasOpenStepApplications(processSteps: JobStep[]): boolean {
    return processSteps.some(
      (step) =>
        (step.type === ProcessStepType.INDIVIDUAL ||
          step.type === ProcessStepType.PRE_QUALIFICATION ||
          step.type === ProcessStepType.INBOX) &&
        step.numOfApplications > 0,
    );
  }

  handleAction(
    promise: Promise<void>,
    action: string,
    dropDown?: DropdownComponent,
  ) {
    if (this.currentAction()) {
      return;
    }
    this.currentAction.set(action);
    promise.finally(() => {
      this.currentAction.set(null);
      dropDown?.close();
    });
  }

  duplicateJob(dropDown?: DropdownComponent) {
    if (this.actionDisabled()) {
      return;
    }
    const job = this.job();
    if (!job) {
      return;
    }
    this.handleAction(this.jobsService.duplicate([job]), 'duplicate', dropDown);
  }

  /**
   * Emits the current job so the parent can open the shared booking modal.
   * @returns {void}
   */
  requestBookDistribution(): void {
    const job = this.job();
    if (!job) {
      return;
    }
    this.bookDistribution.emit(job);
  }

  copyApplicationImportMail(): void {
    this.clipboard.copy(this.job()?.email ?? '');
    this.toastService.success(
      'company-tool.views.jobs.list.toasts.email-copied',
    );
  }

  public regenerateJobSummaryAndScreenings() {
    this.jobsAdapter
      .regenerateJobSummaryAndScreenings(this.job()?.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.toastService.info(
          'company-tool.views.jobs.list.toasts.regenerating-summary-and-screenings',
        );
      });
  }

  public triggerRecommendations() {
    this.jobsAdapter
      .triggerRecommendations(this.job()?.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.toastService.info(
          'company-tool.views.jobs.list.toasts.triggering-recommendations',
        );
      });
  }
}
